import React from "react";
import { Input, Modal, Spin } from "antd";
import {
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  LockOutlined,
  MailOutlined,
  SafetyOutlined,
  WarningOutlined,
  SendOutlined,
  MobileOutlined,
} from "@ant-design/icons";
import ReactInputVerificationCode from "react-input-verification-code";
import styles from "./style.module.scss";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";

const formatTime = (s) =>
  `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;

const Representation = ({
  email,
  setEmail,
  updateChangeEmail,
  saveEmail,
  changeEmail,
  changePassword,
  setShowPasswordModal,
  emailVerified,
  setShowDeleteModal,
  deleteAccount,
  showDeleteModal,
  showPasswordModal,
  setConfirmNewPassword,
  setNewPassword,
  setOldPassword,
  loading,
  disable2fa,
  mfaEnabled,
  plan,
  changePwdLoading,
  resendVerificationEmail,
  resendCooldown,
  // MFA modal
  showMFAModal,
  openMFAModal,
  closeMFAModal,
  mfaPhone,
  mfaSmsSent,
  mfaVerificationCode,
  setMfaVerificationCode,
  mfaTimeLeft,
  mfaLoading,
  mfaSendSMS,
  mfaResend,
  mfaEnable,
}) => {
  const theme = useSelector((state) => state.DashboardReducer.theme);
  const { t } = useTranslation();

  const modalBg =
    theme === "dark" ? "#1C193B" : theme === "light" ? "#ffffff" : "#f4f4f4";
  const modalTextColor = theme === "dark" ? "#ffffff" : "#1a1a1a";

  const ModalStyle = {
    padding: "32px",
    borderRadius: "14px",
    background: modalBg,
    overflow: "hidden",
  };

  return (
    <div className={styles.page}>
      <h2 className={styles.pageTitle}>{t("settings.15")}</h2>
      <p className={styles.pageSubtitle}>{t("settings.16")}</p>

      {/* ── Email Address Card ── */}
      <div className={styles.card}>
        <div className={styles.cardHeader}>
          <MailOutlined className={styles.cardIcon} />
          <div>
            <h3 className={styles.cardTitle}>{t("settings.12")}</h3>
            <p className={styles.cardSubtitle}>
              {t("settings.257")}
            </p>
          </div>
        </div>

        <div className={styles.cardBody}>
          {emailVerified ? (
            <div className={styles.verifiedBadge}>
              <CheckCircleOutlined />
              {t("settings.13")}
            </div>
          ) : (
            <div className={styles.unverifiedRow}>
              <div className={styles.unverifiedBadge}>
                <ExclamationCircleOutlined />
                {t("settings.14")}
              </div>
              <p className={styles.unverifiedHint}>
                {t("settings.259")}
              </p>
              <button
                className={styles.secondaryBtn}
                onClick={resendVerificationEmail}
                disabled={resendCooldown > 0}
              >
                <SendOutlined />
                {resendCooldown > 0
                  ? t("settings.261", { seconds: resendCooldown })
                  : t("settings.260")}
              </button>
            </div>
          )}

          <label className={styles.fieldLabel}>{t("settings.17")}</label>
          <Input
            className={styles.customInput}
            value={email}
            placeholder={t("settings.17")}
            onChange={(e) => setEmail(e.target.value)}
            disabled={!changeEmail}
          />

          {plan?.PackageId !== 68 && (
            <div className={styles.actionRow}>
              <button
                className={styles.outlineBtn}
                onClick={updateChangeEmail}
              >
                {changeEmail ? t("button.3") : t("settings.18")}
              </button>
              {changeEmail && (
                <button
                  className={styles.primaryBtn}
                  onClick={saveEmail}
                >
                  {t("button.16")}
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* ── Password Card ── */}
      <div className={styles.card}>
        <div className={styles.cardHeader}>
          <LockOutlined className={styles.cardIcon} />
          <div>
            <h3 className={styles.cardTitle}>{t("settings.22")}</h3>
            <p className={styles.cardSubtitle}>
              {t("settings.258")}
            </p>
          </div>
        </div>
        <div className={styles.cardBody}>
          <button
            className={styles.primaryBtn}
            onClick={() => setShowPasswordModal(true)}
          >
            {t("settings.19")}
          </button>
        </div>
      </div>

      {/* ── 2FA Card (plan 100 only) ── */}
      {plan?.PackageId === 100 && (
        <div className={styles.card}>
          <div className={styles.cardHeader}>
            <SafetyOutlined className={styles.cardIcon} />
            <div>
              <h3 className={styles.cardTitle}>{t("settings.20")}</h3>
              <p className={styles.cardSubtitle}>{t("settings.21")}</p>
            </div>
          </div>
          <div className={styles.cardBody}>
            {mfaEnabled && (
              <div className={styles.mfaEnabledBadge}>
                <CheckCircleOutlined /> {t("settings.262")}
              </div>
            )}
            <button
              className={mfaEnabled ? styles.dangerOutlineBtn : styles.primaryBtn}
              onClick={() => mfaEnabled ? disable2fa() : openMFAModal()}
            >
              {mfaEnabled ? `${t("settings.23")} 2FA` : `${t("settings.24")} 2FA`}
            </button>
          </div>
        </div>
      )}

      {/* ── 2FA Setup Modal ── */}
      <Modal
        open={showMFAModal}
        footer={null}
        onCancel={closeMFAModal}
        bodyStyle={{ padding: "32px", background: modalBg, borderRadius: "14px" }}
        style={{ borderRadius: "14px", overflow: "hidden", padding: 0 }}
        width={460}
      >
        <div style={{ color: modalTextColor }}>
          <div className={styles.mfaModalHeader}>
            <SafetyOutlined className={styles.mfaShieldIcon} />
            <h4 className={styles.modalTitle} style={{ color: modalTextColor }}>
              {t("settings.24")} 2FA
            </h4>
          </div>

          {/* No phone on account → direct user to Account page */}
          {!mfaPhone ? (
            <>
              <div className={styles.mfaNoPhone}>
                <MobileOutlined className={styles.mfaNoPhoneIcon} />
                <p className={styles.mfaNoPhoneText}>
                  {t("settings.263")}
                </p>
                <a href="/settings/details" className={styles.mfaGoAccountLink}>
                  {t("settings.264")} →
                </a>
              </div>
              <div className={styles.modalActions}>
                <button className={styles.outlineBtn} onClick={closeMFAModal}>
                  {t("settings.265")}
                </button>
              </div>
            </>
          ) : !mfaSmsSent ? (
            /* Step 1: confirm which number receives the code */
            <>
              <p className={styles.modalDesc} style={{ color: modalTextColor }}>
                {t("settings.266")}
              </p>
              <div className={styles.mfaPhoneDisplay}>
                <MobileOutlined className={styles.mfaPhoneDisplayIcon} />
                <span className={styles.mfaPhoneNumber}>{mfaPhone}</span>
              </div>
              <p className={styles.mfaHint}>
                {t("settings.267")}{" "}
                <a href="/settings/details" className={styles.mfaGoAccountLink}>
                  {t("settings.276")}
                </a>
                .
              </p>
              <div className={styles.modalActions}>
                <button className={styles.outlineBtn} onClick={closeMFAModal}>
                  {t("button.3")}
                </button>
                <button
                  className={styles.primaryBtn}
                  onClick={mfaSendSMS}
                  disabled={mfaLoading}
                >
                  {mfaLoading ? <Spin size="small" /> : t("settings.268")}
                </button>
              </div>
            </>
          ) : (
            <>
              <label className={styles.modalFieldLabel} style={{ color: modalTextColor }}>
                {t("settings.269")}
              </label>
              <div className={styles.mfaOtpWrap}>
                <ReactInputVerificationCode
                  length={6}
                  value={mfaVerificationCode}
                  onChange={setMfaVerificationCode}
                  placeholder=""
                />
              </div>
              {mfaTimeLeft > 0 ? (
                <p className={styles.mfaTimer}>
                  {t("settings.270", { time: formatTime(mfaTimeLeft) })}
                </p>
              ) : (
                <button className={styles.mfaResendBtn} onClick={mfaResend} disabled={mfaLoading}>
                  {t("settings.271")}
                </button>
              )}
              <div className={styles.modalActions}>
                <button className={styles.outlineBtn} onClick={closeMFAModal}>
                  {t("button.3")}
                </button>
                <button
                  className={styles.primaryBtn}
                  onClick={mfaEnable}
                  disabled={mfaLoading || !mfaVerificationCode || mfaVerificationCode.length < 6}
                >
                  {mfaLoading ? <Spin size="small" /> : t("settings.272")}
                </button>
              </div>
            </>
          )}
        </div>
      </Modal>

      {/* ── Danger Zone Card ── */}
      <div className={`${styles.card} ${styles.dangerCard}`}>
        <div className={styles.cardHeader}>
          <WarningOutlined className={`${styles.cardIcon} ${styles.dangerIcon}`} />
          <div>
            <h3 className={`${styles.cardTitle} ${styles.dangerTitle}`}>
              {t("settings.25")}
            </h3>
            <p className={styles.cardSubtitle}>{t("settings.26")}</p>
          </div>
        </div>
        <div className={styles.cardBody}>
          <button
            className={styles.dangerBtn}
            onClick={() => setShowDeleteModal(true)}
          >
            {t("settings.27")}
          </button>
        </div>
      </div>

      {/* ── Delete Account Modal ── */}
      <Modal
        open={showDeleteModal}
        bodyStyle={ModalStyle}
        footer={null}
        onCancel={() => setShowDeleteModal(false)}
        style={{ overflow: "hidden", borderRadius: "14px", padding: "0px" }}
      >
        <div style={{ color: modalTextColor }}>
          <div className={styles.modalHeader}>
            <WarningOutlined className={styles.modalWarnIcon} />
            <h4 className={styles.modalTitle} style={{ color: modalTextColor }}>
              {t("settings.28")}
            </h4>
          </div>
          <p className={styles.modalDesc} style={{ color: modalTextColor }}>
            {t("settings.273")}
          </p>
          <div className={styles.deleteEmailBox}>
            <span className={styles.deleteEmailLabel}>{t("settings.274")}</span>
            <a
              href={`mailto:info@pivony.com?subject=Account%20Deletion%20Request&body=Hi%2C%0A%0AI%20would%20like%20to%20request%20the%20deletion%20of%20my%20Pivony%20account.%0A%0AAccount%20email%3A%20${encodeURIComponent(email || "")}`}
              className={styles.deleteEmailLink}
            >
              info@pivony.com
            </a>
          </div>
          <div className={styles.modalActions}>
            <button
              className={styles.outlineBtn}
              onClick={() => setShowDeleteModal(false)}
            >
              {t("button.3")}
            </button>
            <button
              className={styles.dangerBtn}
              onClick={() => {
                window.location.href = `mailto:info@pivony.com?subject=Account%20Deletion%20Request&body=Hi%2C%0A%0AI%20would%20like%20to%20request%20the%20deletion%20of%20my%20Pivony%20account.%0A%0AAccount%20email%3A%20${encodeURIComponent(email || "")}`;
                setShowDeleteModal(false);
              }}
            >
              {t("settings.275")}
            </button>
          </div>
        </div>
      </Modal>

      {/* ── Change Password Modal ── */}
      <Modal
        open={showPasswordModal}
        bodyStyle={ModalStyle}
        footer={null}
        onCancel={() => !changePwdLoading && setShowPasswordModal(false)}
        maskClosable={!changePwdLoading}
        style={{ overflow: "hidden", borderRadius: "14px", padding: "0px" }}
      >
        <div style={{ color: modalTextColor }}>
          <h4 className={styles.modalTitle} style={{ color: modalTextColor }}>
            {t("settings.31")}
          </h4>
          <label className={styles.modalFieldLabel} style={{ color: modalTextColor }}>
            {t("settings.32")}
          </label>
          <Input.Password
            className={styles.customInput}
            placeholder={t("settings.32")}
            onChange={(e) => setOldPassword(e.target.value)}
          />
          <label className={styles.modalFieldLabel} style={{ color: modalTextColor }}>
            {t("settings.33")}
          </label>
          <Input.Password
            className={styles.customInput}
            placeholder={t("settings.34")}
            onChange={(e) => setNewPassword(e.target.value)}
          />
          <label className={styles.modalFieldLabel} style={{ color: modalTextColor }}>
            {t("settings.35")}
          </label>
          <Input.Password
            className={styles.customInput}
            placeholder={t("settings.36")}
            onChange={(e) => setConfirmNewPassword(e.target.value)}
          />
          <div className={styles.modalActions}>
            <button
              className={styles.outlineBtn}
              onClick={() => !changePwdLoading && setShowPasswordModal(false)}
            >
              {t("button.2")}
            </button>
            <button
              className={styles.primaryBtn}
              disabled={changePwdLoading}
              onClick={changePassword}
            >
              {changePwdLoading ? <Spin size="small" /> : t("button.4")}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default Representation;
